"use client"

import React, { useState, useEffect, useCallback } from "react"
import { motion } from "framer-motion"
import { X, Banknote, ArrowLeftRight, CreditCard, CheckCircle, AlertTriangle, Gift, Clock } from "lucide-react"
import { getTarifaMontoDetalle, cobrarTicket, getSucursalTicketInfo } from "../services/controlService"
import { imprimirTicketSalida, parseFechaBackend, fmtFecha, fmtHora as horaTicket } from "../utils/ticketPrint"
import "./CobroModal.css"

/* Métodos de pago: etiqueta UI -> código backend */
const METODOS = [
  { code: "E", label: "Efectivo", icon: Banknote },
  { code: "T", label: "Transferencia", icon: ArrowLeftRight },
  { code: "TC", label: "Tarjeta", icon: CreditCard },
]

/**
 * Hora localizada para la UI. El ticket impreso usa HH:mm:ss (horaTicket),
 * y el parseo de fechas del backend vive en utils/ticketPrint.
 */
const fmtHora = (s) => {
  const d = parseFechaBackend(s)
  return d ? d.toLocaleString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"
}
const money = (v) => (v == null || isNaN(Number(v)) ? "0.00" : Number(v).toFixed(2))

const CobroModal = ({ vehiculo, sucursalId, role, onClose, onSuccess }) => {
  const [status, setStatus] = useState("loading") // loading | selecting | processing | success | error
  const [detalle, setDetalle] = useState(null)
  const [metodo, setMetodo] = useState(null)
  const [gratis, setGratis] = useState(false)
  const [error, setError] = useState("")
  const [sucursal, setSucursal] = useState(null)

  const esAdmin = role === "ADMIN" || role === "DEVELOPER"

  const cargarDetalle = useCallback(async () => {
    try {
      setStatus("loading")
      setError("")
      // La cabecera del ticket se pide junto con el monto para no demorar
      // la impresión después; si falla, el cobro sigue igual.
      const [data, info] = await Promise.all([
        getTarifaMontoDetalle(vehiculo.placa, sucursalId),
        getSucursalTicketInfo(sucursalId).catch(() => null),
      ])
      setDetalle(data)
      setSucursal(info)
      setStatus("selecting")
    } catch (e) {
      setError(e.message || "No se pudo calcular el monto.")
      setStatus("error")
    }
  }, [vehiculo.placa, sucursalId])

  useEffect(() => { cargarDetalle() }, [cargarDetalle])

  const montoEfectivo = gratis ? 0 : Number(detalle?.monto ?? 0)

  /**
   * Imprime el ticket de salida con los mismos datos que la app móvil
   * (printSalida): placa, fecha, entrada, salida y total cobrado.
   * La fecha del comprobante es la de la salida.
   */
  const imprimirComprobante = async () => {
    const salida = parseFechaBackend(detalle.hsalida) || new Date()
    const entrada = parseFechaBackend(detalle.hentrada)
    const info = sucursal ?? (await getSucursalTicketInfo(sucursalId).catch(() => null))

    await imprimirTicketSalida({
      sucursal: info,
      placa: vehiculo.placa,
      fecha: fmtFecha(salida),
      horaEntrada: entrada ? horaTicket(entrada) : "—",
      horaSalida: horaTicket(salida),
      monto: montoEfectivo,
    })
  }

  const procesar = async () => {
    if (!metodo || !detalle) return
    try {
      setStatus("processing")
      setError("")
      await cobrarTicket(detalle.id, { mfinal: montoEfectivo, tpago: metodo })
      setStatus("success")

      // El cobro ya quedó registrado: un fallo de impresión no debe
      // deshacerlo ni dejar el modal colgado.
      try {
        await imprimirComprobante()
      } catch (e) {
        console.error("No se pudo imprimir el ticket de salida:", e)
      }

      setTimeout(() => {
        onSuccess?.(vehiculo.id)
        onClose?.()
      }, 1400)
    } catch (e) {
      setError(e.message || "No se pudo procesar el cobro.")
      setStatus("error")
    }
  }

  return (
    <div className="cb-overlay" onClick={onClose}>
      <motion.div
        className="cb-modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
      >
        <div className="cb-header">
          <h3><Banknote size={20} /> Cobrar · <span className="cb-placa">{vehiculo.placa}</span></h3>
          <button className="cb-icon-btn" onClick={onClose} aria-label="Cerrar"><X size={20} /></button>
        </div>

        {status === "loading" && (
          <div className="cb-state"><div className="cb-spinner" /> Calculando monto…</div>
        )}

        {status === "error" && (
          <div className="cb-body">
            <div className="cb-error"><AlertTriangle size={18} /> {error}</div>
            <div className="cb-actions">
              <button className="cb-btn cb-btn-ghost" onClick={onClose}>Cerrar</button>
              <button className="cb-btn cb-btn-primary" onClick={cargarDetalle}>Reintentar</button>
            </div>
          </div>
        )}

        {(status === "selecting" || status === "processing") && detalle && (
          <div className="cb-body">
            {/* Resumen */}
            <div className="cb-resumen">
              <div className="cb-resumen-row">
                <span><Clock size={14} /> Entrada</span>
                <strong>{fmtHora(detalle.hentrada)}</strong>
              </div>
              <div className="cb-resumen-row">
                <span><Clock size={14} /> Salida</span>
                <strong>{fmtHora(detalle.hsalida)}</strong>
              </div>
            </div>

            {/* Monto */}
            <div className={`cb-monto ${gratis ? "gratis" : ""}`}>
              <span className="cb-monto-label">Monto a cobrar</span>
              <span className="cb-monto-valor">${money(montoEfectivo)}</span>
              {gratis && <span className="cb-monto-original">Original: ${money(detalle.monto)}</span>}
            </div>

            {/* Método de pago */}
            <div className="cb-metodos-label">Método de pago</div>
            <div className="cb-metodos">
              {METODOS.map((m) => {
                const Icon = m.icon
                return (
                  <button
                    key={m.code}
                    className={`cb-metodo ${metodo === m.code ? "active" : ""}`}
                    onClick={() => setMetodo(m.code)}
                    disabled={status === "processing"}
                  >
                    <Icon size={20} />
                    <span>{m.label}</span>
                  </button>
                )
              })}
            </div>

            {/* Gratis (solo ADMIN/DEVELOPER) */}
            {esAdmin && (
              <label className="cb-gratis">
                <input type="checkbox" checked={gratis} onChange={(e) => setGratis(e.target.checked)} disabled={status === "processing"} />
                <Gift size={16} /> Cobrar GRATIS ($0.00)
              </label>
            )}

            {error && <div className="cb-error"><AlertTriangle size={18} /> {error}</div>}

            <div className="cb-actions">
              <button className="cb-btn cb-btn-ghost" onClick={onClose} disabled={status === "processing"}>Cancelar</button>
              <button className="cb-btn cb-btn-primary" onClick={procesar} disabled={!metodo || status === "processing"}>
                {status === "processing" ? (<><span className="cb-mini-spinner" /> Procesando…</>) : (<>Cobrar ${money(montoEfectivo)}</>)}
              </button>
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="cb-state cb-success">
            <CheckCircle size={64} />
            <p>¡Cobro realizado!</p>
            <span className="cb-success-sub">Imprimiendo ticket de salida…</span>
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default CobroModal
